import { Outlet } from 'react-router-dom'
import loadable from '@loadable/component'
import { PATIENTS, PROVIDERS } from '../route.const'
import { PageProps } from '@config/typing'
import providerPrivacy from '@config/provider/privacy.json'
import patientPrivacy from '@config/patient/privacy.json'
import patientMinor from '@config/patient/minor.json'
import patientPaywall from '@config/patient/paywall.json'
import notFoundContent from '@config/misc/not-found.json'
import activityContent from '@config/patient/activity.json'
import expectationContent from '@config/patient/expectation.json'
import providerExpectationContent from '@config/provider/expectation.json'
import { NotFoundProps } from '@common/NotFound'
import { IExpectationProps } from '@patient/Expectation'
import { IActivityProps } from '@patient/Activity'
import { OMRouteConfig } from './typing'
import StartSurvey from '@survey/StartSurvey'
import ProviderWelcome from '@provider/welcome'

/* Root components */
const Home = loadable(() => import('../components/Home'))
const OBGYNLandingPage = loadable(
  () => import('../components/Specialties/OBGYN')
)
const CampaignLanding = loadable(() => import('../components/CampaignLanding'))
const Survey = loadable(() => import('../components/Survey'))
const Provider = loadable(() => import('../components/Provider'))
const Patient = loadable(() => import('../components/Patient'))
const ContactPage = loadable(() => import('../components/Contact'))
const Admin = loadable(() => import('../components/Admin'))

/* Common components */
const Privacy = loadable(() => import('@common/Privacy'))
const PrivacyPolicy = loadable(
  () => import('@common/PrivacyPolicy/PrivacyPolicy')
)
const TermsOfUse = loadable(() => import('@common/TermsOfUse/TermsOfUse'))

const NotAvailable = loadable(() => import('@common/NotAvailable'))
const AboutUsWrapper = loadable(() => import('@common/AboutUsWrapper'))
const LoginConfirmation = loadable(() => import('@common/LoginConfirmation'))
const NotFound = loadable(() => import('@common/NotFound'))
const FAQ = loadable(() => import('@common/FAQ'))
const AccountSettings = loadable(() => import('@common/AccountSettings'))

/* Admin components*/
const RequireAdminLogin = loadable(() => import('@admin/RequireLogin'))
const AdminPanel = loadable(() => import('@admin/controlPanel'))
const Organizations = loadable(() => import('@admin/Organizations'))
const Clinician = loadable(() => import('@admin/Clinician'))
const ServiceLocation = loadable(
  () => import('@admin/ServiceLocations/AddServiceLocation')
)
const SyncSurvey = loadable(() => import('@admin/SyncSurvey'))

/* Provider components*/
const ProviderHome = loadable(() => import('@provider/Home'))
const ProviderLogin = loadable(() => import('@provider/Login'))
const RequireProviderLogin = loadable(() => import('@provider/RequireLogin'))
const Dashboard = loadable(() => import('@provider/Dashboard'))
const ProviderProfile = loadable(() => import('@provider/Profile'))
const MyPatients = loadable(() => import('@provider/Patients/MyPatients'))
const MyMatches = loadable(() => import('@provider/Patients/MyMatches'))
/*Patient components*/
const PatientLogin = loadable(() => import('@patient/Login'))
const RequirePatientLogin = loadable(() => import('@patient/RequireLogin'))
const ProviderSearch = loadable(() => import('@patient/BeginSearch'))
const PatientNotAvailable = loadable(() => import('@patient/NotAvailable'))
const PatientMinor = loadable(() => import('@patient/Minor'))
const PatientPaywall = loadable(() => import('@patient/Paywall'))
const SearchResult = loadable(() => import('@patient/SearchResult'))
const Expectation = loadable(() => import('@patient/Expectation'))
const MultiSurveySelection = loadable(
  () => import('@patient/MultiSurveySelection')
)
const Activity = loadable(() => import('@patient/Activity'))
const PatientHome = loadable(() => import('@patient/Home'))
const PatientProfile = loadable(() => import('@patient/Profile'))
const PatientWelcome = loadable(() => import('@patient/Welcome'))
const ViewProviderProfile = loadable(
  () => import('@patient/SearchResult/ViewProviderProfile')
)
const PatientDisclaimer = loadable(() => import('@patient/Disclaimer'))
/* Survey components */
const Category = loadable(() => import('@survey/Category'))
const SurveyIntro = loadable(() => import('@survey/SurveyIntro'))
const Question = loadable(() => import('@survey/Category/Question'))
const SurveyReview = loadable(() => import('@survey/SurveyReview'))
const SurveyComplete = loadable(() => import('@survey/SurveyComplete'))

const providerPrivacyContent: PageProps = providerPrivacy.data as PageProps
const patientPrivacyContent: PageProps = patientPrivacy.data as PageProps
const patientMinorContent: PageProps = patientMinor.data as PageProps
const patientPaywallContent: PageProps = patientPaywall.data as PageProps
const notFound = notFoundContent.data as NotFoundProps
const patientExpectation = expectationContent.data as IExpectationProps
const providerExpectation = providerExpectationContent.data as IExpectationProps
const patientActivity = activityContent.data as IActivityProps

const location = window.location.href

const surveyRouteConfig = (isPatient: boolean): OMRouteConfig => ({
  path: 'survey',
  breadcrumbConfig: { primaryText: '' },
  pageName: 'Survey',
  element: isPatient ? (
    <Survey />
  ) : (
    <RequireProviderLogin>
      <Survey />
    </RequireProviderLogin>
  ),
  siteMapConfig: {
    priority: 0.8,
    changeFrequency: 'weekly',
  },
  pageTitlesConfig: {
    shortTitle: 'Patient survey | Optum Match',
    longTitle: 'Patient onboarding - Optum Match: Start your journey here.',
    description:
      'Welcome to Optum Match! Begin your patient onboarding process to connect with a doctor. Complete the quick survey to see your results.',
  },
  children: [
    {
      path: '',
      element: !isPatient ? <StartSurvey /> : <Survey />,
      pageName: 'Survey Start',
      siteMapConfig: {
        priority: 0.8,
        changeFrequency: 'weekly',
      },
    },
    {
      path: ':sectionId',
      breadcrumbConfig: { primaryText: 'Section id' },
      element: <Outlet />,
      pageName: 'Survey Section',
      children: [
        {
          path: '',
          element: <SurveyIntro />,
          pageName: 'Survey Intro',
        },
        {
          path: 'questionnaire',
          element: <Category />,
          pageName: 'Survey Category',
          children: [
            {
              path: ':questionId',
              breadcrumbConfig: { primaryText: 'Question id' },
              element: <Question />,
              pageName: 'Survey Question',
            },
          ],
        },
      ],
    },
    {
      path: 'review',
      breadcrumbConfig: { primaryText: isPatient ? 'My responses' : '' },
      element: <SurveyReview />,
      pageName: 'Survey Review',
      pageTitlesConfig: {
        shortTitle: 'Review survey | Optum Match',
        longTitle: 'Review survey - Optum Match: Review your respones.',
        description:
          'Review and edit your compatibility survey so your search results are accurate to your priorities and preferences.',
      },
    },
    {
      path: 'feedback',
      breadcrumbConfig: { primaryText: 'Feedback' },
      pageName: 'Survey Feedback',
      element: <SurveyComplete />,
    },
  ],
})

const routesConfig: OMRouteConfig[] = [
  {
    path: PROVIDERS,
    breadcrumbConfig: {
      primaryText: 'Welcome',
      secondaryText: '',
    },
    pageName: location.includes('providers/not-authorized')
      ? 'Clinician- Access Issue'
      : 'Provider Dashboard',
    element: <Provider />,
    children: [
      {
        path: '',
        element: <ProviderHome />,
        pageName: 'Provider Home',
        children: [
          {
            path: 'expectation',
            breadcrumbConfig: { primaryText: 'What to expect' },
            element: <Expectation pageData={providerExpectation} />,
            pageName: 'Providers -- Expectation',
          },
          {
            path: 'welcome/*',
            element: <ProviderWelcome />,
            pageName: 'Provider Welcome',
            siteMapConfig: {
              priority: 0.8,
              changeFrequency: 'weekly',
            },
          },
          {
            path: '',
            element: (
              <RequireProviderLogin>
                <Dashboard />
              </RequireProviderLogin>
            ),
            pageName: 'Clinician Dashboard',
            siteMapConfig: {
              priority: 0.8,
              changeFrequency: 'weekly',
            },
            pageTitlesConfig: {
              shortTitle: 'Dashboard | Optum Match',
              longTitle: 'Dashboard - Optum Match: Understand your matches.',
              description:
                'Access the Clinician Dashboard to keep your professional profile up-to-date, view patient matches, and understand insights about your practice.',
            },
          },
          {
            path: 'privacy',
            element: <Privacy content={providerPrivacyContent} />,
            pageName: 'Clinician Privacy',
            siteMapConfig: {
              priority: 0.8,
              changeFrequency: 'weekly',
            },
            pageTitlesConfig: {
              shortTitle: 'Clinician survey | Optum Match',
              longTitle:
                'Clinician onboarding - Optum Match: Start your journey here.',
              description:
                'Welcome to Optum Match! Begin your clinician onboarding process so patients can find you. Complete the quick survey so patients can begin matching with you.',
            },
          },
          {
            path: 'profile',
            breadcrumbConfig: { primaryText: '' },
            pageName: 'Clinician Profile',
            element: (
              <RequireProviderLogin>
                <ProviderProfile />
              </RequireProviderLogin>
            ),
            siteMapConfig: {
              priority: 0.8,
            },
            pageTitlesConfig: {
              shortTitle: 'Profile | Optum Match',
              longTitle:
                'My profile - Optum Match: Manage your professional profile.',
              description:
                'Update and manage your professional profile on Optum Match. Ensure your details are accurate to help patients find and connect with your more effectively.',
            },
          },
          {
            path: 'no-login',
            element: <LoginConfirmation root={PROVIDERS} />,
            pageName: 'Clinician No Login',
          },
          surveyRouteConfig(false),
          {
            path: 'my-patients',
            breadcrumbConfig: { primaryText: 'My Patients' },
            element: <MyPatients />,
            pageName: 'My Patients',
          },
          {
            path: 'my-matches',
            breadcrumbConfig: { primaryText: 'My Matches' },
            element: <MyMatches />,
            pageName: 'Clinician My Matches',
          },
          {
            path: 'not-authorized',
            breadcrumbConfig: {
              primaryText: 'Access issue',
            },
            pageName: 'Clinician Not Authorized',
            element: <NotAvailable />,
          },
        ],
      },
      {
        path: 'login',
        element: <ProviderLogin />,
        pageName: 'Clinician Login',
        siteMapConfig: {
          priority: 0.8,
          changeFrequency: 'weekly',
        },
      },
    ],
  },
  {
    path: PATIENTS,
    breadcrumbConfig: {
      primaryText: 'Welcome',
      secondaryText: 'My matches',
    },
    pageName: 'Patient Dashboard',
    element: <Patient />,
    children: [
      {
        path: '',
        element: <PatientHome />,
        pageName: 'Patient Home',
        siteMapConfig: {
          priority: 0.8,
          changeFrequency: 'weekly',
        },
      },

      {
        path: 'welcome/*',
        element: <PatientWelcome />,
        pageName: 'Patient Welcome',
        siteMapConfig: {
          priority: 0.8,
          changeFrequency: 'weekly',
        },
      },

      {
        path: 'search-result',
        element: <Outlet />,
        siteMapConfig: {
          priority: 0.8,
          changeFrequency: 'weekly',
        },
        pageTitlesConfig: {
          shortTitle: 'Search results | Optum Match',
          longTitle:
            'Search results - Optum Match: Find a doctor who gets you.',
          description:
            'Explore the search results on Optum Match to find the doctor that fit your needs. Browse detailed profiles, your compatibility score, and match with someone who is right for you.',
        },
        children: [
          {
            path: '',
            element: (
              <RequirePatientLogin>
                <SearchResult />
              </RequirePatientLogin>
            ),
            pageName: 'Search Results',
          },
          {
            path: 'provider-profile/:id',
            breadcrumbConfig: { primaryText: 'Provider Profile' },
            element: (
              <RequirePatientLogin>
                <ViewProviderProfile />
              </RequirePatientLogin>
            ),
            pageName: 'Provider Profile View (Patient)',
          },
        ],
      },
      {
        path: 'profile',
        breadcrumbConfig: { primaryText: 'My profile' },
        pageName: 'Patient Profile',
        element: (
          <RequirePatientLogin>
            <PatientProfile />
          </RequirePatientLogin>
        ),
        siteMapConfig: {
          priority: 0.8,
          changeFrequency: 'weekly',
        },
      },

      {
        path: 'activity',
        breadcrumbConfig: { primaryText: 'My activity' },
        pageName: 'Patient Activity',
        element: <Outlet />,
        siteMapConfig: {
          priority: 0.8,
          changeFrequency: 'weekly',
        },
        pageTitlesConfig: {
          shortTitle: 'My activity | Optum Match',
          longTitle: 'My activity - Optum Match: Track your journey.',
          description:
            'Stay up-to-date with your journey on Optum Match. View recent activities and actions.',
        },
        children: [
          {
            path: '',
            pageName: 'Patient Activity',
            element: (
              <RequirePatientLogin>
                <Activity pageData={patientActivity} />
              </RequirePatientLogin>
            ),
          },
          {
            path: 'provider-profile/:id',
            breadcrumbConfig: { primaryText: 'Provider Profile' },
            element: (
              <RequirePatientLogin>
                <ViewProviderProfile />
              </RequirePatientLogin>
            ),
            pageName: 'Provider Profile View (Patient)',
          },
        ],
      },
      {
        path: 'survey-selection',
        breadcrumbConfig: { primaryText: 'What to expect' },
        element: <MultiSurveySelection />,
        pageName: 'Patient -- MultiSpeciality Selection',
      },
      {
        path: 'expectation',
        breadcrumbConfig: { primaryText: 'What to expect' },
        element: <Expectation pageData={patientExpectation} />,
        pageName: 'Patient -- Expectation',
      },
      {
        path: 'minor',
        breadcrumbConfig: { primaryText: 'Minor' },
        element: <PatientMinor content={patientMinorContent} />,
        pageName: 'Patient Minor',
      },
      {
        path: 'accesswall',
        breadcrumbConfig: { primaryText: 'My matches' },
        element: <PatientPaywall content={patientPaywallContent} />,
        pageName: 'Patient Access Wall',
      },
      {
        path: 'privacy',
        element: <Privacy content={patientPrivacyContent} />,
        pageName: 'Patient Privacy',
        siteMapConfig: {
          priority: 0.8,
          changeFrequency: 'weekly',
        },
      },
      {
        path: 'login',
        breadcrumbConfig: { primaryText: 'Login' },
        element: <PatientLogin />,
        pageName: 'Patient Login',
        siteMapConfig: {
          priority: 0.8,
          changeFrequency: 'weekly',
        },
      },
      {
        path: 'not-available',
        breadcrumbConfig: { primaryText: 'Not available' },
        element: <PatientNotAvailable />,
        pageName: 'Patient Not Available',
      },
      {
        path: 'no-login',
        breadcrumbConfig: { primaryText: 'No Login' },
        element: <LoginConfirmation root={PATIENTS} />,
        pageName: 'Patient No Login',
      },
      {
        path: 'begin-search',
        breadcrumbConfig: { primaryText: 'Start' },
        element: <ProviderSearch />,
        pageName: 'Patient -- Zip code entry',
        siteMapConfig: {
          priority: 0.8,
          changeFrequency: 'weekly',
        },
      },
      {
        path: 'disclaimer',
        breadcrumbConfig: { primaryText: 'Disclaimer' },
        element: <PatientDisclaimer />,
        pageName: 'Disclaimer page',
        siteMapConfig: {
          priority: 0.8,
          changeFrequency: 'weekly',
        },
      },
      surveyRouteConfig(true),
    ],
  },
  {
    path: 'about-us',
    breadcrumbConfig: { primaryText: 'About us' },
    pageName: 'About Us',
    element: <AboutUsWrapper />,
    siteMapConfig: {
      priority: 0.8,
      changeFrequency: 'weekly',
    },
    pageTitlesConfig: {
      shortTitle: 'About us | Optum Match',
      longTitle:
        'About us - Optum Match: Connecting patients with doctors who get their unique health care needs.',
      description:
        "Learn about Optum Match, where we connect patients with doctors based on their unique health care needs. We're here to make the process simple, the connections authentic, and the results lasting.",
    },
  },
  {
    path: 'faq',
    breadcrumbConfig: { primaryText: 'FAQ' },
    pageName: 'FAQ',
    element: <FAQ />,
    siteMapConfig: {
      priority: 0.8,
      changeFrequency: 'weekly',
    },
    pageTitlesConfig: {
      shortTitle: 'FAQ | Optum Match',
      longTitle: 'FAQ - Optum Match: Frequently asked questions.',
      description: 'Review frequently asked questions about Optum Match.',
    },
  },
  {
    path: 'privacy-policy',
    element: <PrivacyPolicy />,
    pageName: 'Privacy Policy',
    siteMapConfig: {
      priority: 0.8,
      changeFrequency: 'weekly',
    },
    pageTitlesConfig: {
      shortTitle: 'Privacy Policy | Optum Match',
      longTitle: 'Privacy Policy - Optum Match: Privacy policy function.',
      description: 'Review privacy policy page.',
    },
  },
  {
    path: 'settings',
    breadcrumbConfig: { primaryText: 'Account settings' },
    pageName: 'AccountSettings',
    element: <AccountSettings />,
    siteMapConfig: {
      priority: 0.8,
      changeFrequency: 'weekly',
    },
    pageTitlesConfig: {
      shortTitle: 'Account Settings | Optum Match',
      longTitle: 'Settings - Optum Match: Account settings function.',
      description: 'Review logged in user account details and manage .',
    },
  },
  {
    path: 'admin',
    element: <Outlet />,
    breadcrumbConfig: { primaryText: 'Admin' },
    pageName: 'Admin',
    siteMapConfig: {
      priority: 0.8,
      changeFrequency: 'weekly',
    },
    pageTitlesConfig: {
      shortTitle: 'Admin | Optum Match',
      longTitle: 'Admin - Optum Match: Admin function.',
      description: 'Admin function for Optum Match.',
    },
    children: [
      {
        path: '',
        element: <Admin />,
        pageName: 'Admin Home',
        siteMapConfig: {
          priority: 0.8,
          changeFrequency: 'weekly',
        },
        pageTitlesConfig: {
          shortTitle: 'Admin Home',
          longTitle: 'Admin - Home: Admin function.',
          description: 'Admin function for Optum Match.',
        },
      },
      {
        path: 'sync-survey',
        element: (
          <RequireAdminLogin>
            <SyncSurvey />
          </RequireAdminLogin>
        ),
        breadcrumbConfig: { primaryText: 'Sync Survey JSON' },
        pageName: 'Sync Survey',
        siteMapConfig: {
          priority: 0.8,
          changeFrequency: 'weekly',
        },
        pageTitlesConfig: {
          shortTitle: 'Sync Survey JSON | Optum Match',
          longTitle: 'Sync Survey JSON - Optum Match: Admin function.',
          description: 'Synchronize survey JSON data from backend.',
        },
      },
      {
        path: 'organizations',
        element: <Outlet />,
        children: [
          {
            path: '',
            element: (
              <RequireAdminLogin>
                <AdminPanel />
              </RequireAdminLogin>
            ), // This will render for /admin/organizations
            breadcrumbConfig: { primaryText: 'Organizations' },
            pageName: 'Organizations',
            siteMapConfig: {
              priority: 0.8,
              changeFrequency: 'weekly',
            },
            pageTitlesConfig: {
              shortTitle: 'Organizations | Optum Match',
              longTitle: 'Organizations - Optum Match: Admin function.',
              description: 'Admin function for Optum Match.',
            },
          },
          {
            path: 'add',
            element: (
              <RequireAdminLogin>
                <Organizations />
              </RequireAdminLogin>
            ),
            breadcrumbConfig: { primaryText: 'Add organization' },
            pageName: 'Add Organization',
            siteMapConfig: {
              priority: 0.8,
              changeFrequency: 'weekly',
            },
            pageTitlesConfig: {
              shortTitle: 'Add organization | Optum Match',
              longTitle: 'Add organization - Optum Match: Admin function.',
              description: 'Admin function for Optum Match.',
            },
          },
          {
            path: ':organizationId',
            element: <Outlet />, // Nested routes for organization-specific pages
            breadcrumbConfig: { primaryText: 'Organization details' },
            children: [
              {
                path: '',
                element: (
                  <RequireAdminLogin>
                    <Organizations />
                  </RequireAdminLogin>
                ), // This will render for /admin/organizations/:id
                breadcrumbConfig: { primaryText: 'Organization details' },
                pageName: 'Organization Details',
                siteMapConfig: {
                  priority: 0.8,
                  changeFrequency: 'weekly',
                },
                pageTitlesConfig: {
                  shortTitle: 'Organization details | Optum Match',
                  longTitle:
                    'Organization details - Optum Match: Admin function.',
                  description: 'Admin function for Optum Match.',
                },
              },
              {
                path: 'service-locations',
                element: <Outlet />, // Change to Outlet to support nested routes
                breadcrumbConfig: { primaryText: 'Service locations' },
                children: [
                  {
                    path: '', // Empty path for the service-locations list view
                    element: (
                      <RequireAdminLogin>
                        <Organizations />
                      </RequireAdminLogin>
                    ),
                    breadcrumbConfig: { primaryText: 'Service locations' },
                    pageName: 'Service Locations',
                    siteMapConfig: {
                      priority: 0.8,
                      changeFrequency: 'weekly',
                    },
                    pageTitlesConfig: {
                      shortTitle: 'Service Locations | Optum Match',
                      longTitle:
                        'Service Locations - Optum Match: Admin function.',
                      description: 'Admin function for Optum Match.',
                    },
                  },
                  {
                    path: 'add',
                    element: (
                      <RequireAdminLogin>
                        <ServiceLocation />
                      </RequireAdminLogin>
                    ), // This will render for /admin/organizations/:id/location/add
                    breadcrumbConfig: { primaryText: 'Add service location' },
                    pageName: 'Add Location',
                    siteMapConfig: {
                      priority: 0.8,
                      changeFrequency: 'weekly',
                    },
                    pageTitlesConfig: {
                      shortTitle: 'Add location | Optum Match',
                      longTitle: 'Add location - Optum Match: Admin function.',
                      description: 'Admin function for Optum Match.',
                    },
                  },
                  {
                    path: ':locationId',
                    element: (
                      <RequireAdminLogin>
                        <ServiceLocation />
                      </RequireAdminLogin>
                    ), // This will now render for /admin/organizations/:id/service-locations/:locationId
                    breadcrumbConfig: { primaryText: 'Location details' },
                    pageName: 'Location Details',
                    siteMapConfig: {
                      priority: 0.8,
                      changeFrequency: 'weekly',
                    },
                  },
                ],
              },
              {
                path: 'clinicians',
                element: <Outlet />, // Change to Outlet to support nested routes
                breadcrumbConfig: { primaryText: 'Clinicians' },
                children: [
                  {
                    path: '', // Empty path for the service-locations list view
                    element: (
                      <RequireAdminLogin>
                        <Organizations />
                      </RequireAdminLogin>
                    ),
                    breadcrumbConfig: { primaryText: 'Clinicians' },
                    pageName: 'Clinicians',
                    siteMapConfig: {
                      priority: 0.8,
                      changeFrequency: 'weekly',
                    },
                    pageTitlesConfig: {
                      shortTitle: 'Clinicians | Optum Match',
                      longTitle: 'Clinicians - Optum Match: Admin function.',
                      description: 'Admin function for Optum Match.',
                    },
                  },
                  {
                    path: 'add',
                    element: (
                      <RequireAdminLogin>
                        <Clinician />
                      </RequireAdminLogin>
                    ), // This will render for /admin/organizations/:id/location/add
                    breadcrumbConfig: { primaryText: 'Add clinicians' },
                    pageName: 'Add Clinician',
                    siteMapConfig: {
                      priority: 0.8,
                      changeFrequency: 'weekly',
                    },
                    pageTitlesConfig: {
                      shortTitle: 'Add clinician | Optum Match',
                      longTitle: 'Add clinician - Optum Match: Admin function.',
                      description: 'Admin function for Optum Match.',
                    },
                  },
                  {
                    path: ':clinicianId',
                    element: (
                      <RequireAdminLogin>
                        <Clinician />
                      </RequireAdminLogin>
                    ), // This will now render for /admin/organizations/:id/clinicians/:clinicianId
                    breadcrumbConfig: { primaryText: 'Clinician details' },
                    pageName: 'Clinician Details',
                    siteMapConfig: {
                      priority: 0.8,
                      changeFrequency: 'weekly',
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: 'lp',
    pageName: 'Landing Pages',
    element: <Outlet />,
    children: [
      {
        path: 'ob-gyn',
        element: <OBGYNLandingPage />,
        pageName: 'OB-GYN Landing Page',
        analyticsTag: 'om_obgyn_landing',
        siteMapConfig: {
          priority: 0.9,
          changeFrequency: 'weekly',
        },
        pageTitlesConfig: {
          shortTitle: 'OB-GYN Match | Optum Match',
          longTitle: 'Optum Match: Find an OB-GYN who understands you.',
          description:
            'Take the 10-minute survey to get matched with an OB-GYN who aligns with your values, care preferences, and communication style.',
        },
      },
      {
        path: ':campaignSlug',
        element: <CampaignLanding />,
        pageName: 'Campaign Landing',
        analyticsTag: 'om_ocwa-virtual_campaign',
        siteMapConfig: {
          priority: 0.9,
          changeFrequency: 'weekly',
        },
        pageTitlesConfig: {
          shortTitle: 'Find Your Match | Optum Match',
          longTitle:
            'Optum Match: Match with a primary care doctor who gets you.',
          description:
            'Take the 10-minute survey to find and match with a primary care doctor who truly understands your needs and preferences.',
        },
      },
    ],
  },
  {
    path: '',
    element: <Home />,
    pageName: 'Landing Page',
    siteMapConfig: {
      priority: 1.0,
      changeFrequency: 'weekly',
    },
    pageTitlesConfig: {
      shortTitle: 'Home | Optum Match',
      longTitle: 'Optum Match: Match with a doctor based on your unique needs.',
      description:
        'Discover Optum Match, a powerful tool designed to match patients with doctors. Learn how you can take a quick survey, get matched, and find a doctor who is right for you. ',
    },
  },
  {
    path: 'contact-us',
    breadcrumbConfig: { primaryText: 'Contact us' },
    element: <ContactPage />,
    pageName: 'Contact Us',
    siteMapConfig: {
      priority: 0.8,
      changeFrequency: 'weekly',
    },
    pageTitlesConfig: {
      shortTitle: 'Contact us | Optum Match',
      longTitle: 'Contact us - Optum Match: Get in touch with our team.',
      description:
        'Have questions or need assistance? Contact our team for support and inquiries.',
    },
  },
  {
    path: '*',
    element: <NotFound notFound={notFound} />,
    pageName: 'Not Available',
    pageTitlesConfig: {
      shortTitle: '404 | Optum Match',
      longTitle: '404 - Optum Match: For pages  not found or with errors.',
      description:
        'This is an Optum Match support page for pages that have a problem loading or are not found.',
    },
  },
  {
    path: 'not-found',
    element: <NotFound notFound={notFound} />,
    pageName: 'Not Found',
    pageTitlesConfig: {
      shortTitle: '404 | Optum Match',
      longTitle: '404 - Optum Match: For pages  not found or with errors.',
      description:
        'This is an Optum Match support page for pages that have a problem loading or are not found.',
    },
  },

  {
    path: 'terms-of-use',
    breadcrumbConfig: { primaryText: 'Terms of use' },
    pageName: 'Terms of use',
    element: <TermsOfUse />,
    siteMapConfig: {
      priority: 0.8,
      changeFrequency: 'weekly',
    },
    pageTitlesConfig: {
      shortTitle: 'Terms of use | Optum Match',
      longTitle: 'Terms of use - Optum Match',
      description: 'Review the terms of use about Optum Match.',
    },
  },
]

export default routesConfig
